import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import TransitRouteStopsList from '@features/public-transit-editor/transit-management/TransitRouteStopsList/TransitRouteStopsList';
import TransitRouteEdit from '@features/public-transit-editor/transit-management/TransitRoutes/TransitRouteEdit';
import TransitRouteDeparturesList from '@features/public-transit-editor/transit-management/TransitRouteDeparturesList/TransitRouteDeparturesList';
import DepartureEdit from '@features/public-transit-editor/transit-management/Departures/DepartureEdit';
import TransitLinesList from '@features/public-transit-editor/transit-management/TransitLines/TransitLinesList';
import TransitLineEdit from '@features/public-transit-editor/transit-management/TransitLines/TransitLineEdit';
import TransitLine from '@features/public-transit-editor/transit-management/TransitLines/TransitLine';
import TransitRoutesList from '@features/public-transit-editor/transit-management/TransitRoutes/TransitRoutesList';
import TransitRoute from '@features/public-transit-editor/transit-management/TransitRoutes/TransitRoute';
import { useTransitStopsRoutes } from '@features/public-transit-editor/transit-management/TransitStops/Routes';
import LayersListWrapper from '@features/map-layers-list/layers-list/LayersListWrapper';
import LayersTimeSelectors from '@features/map-layers-list/time-selector/LayersTimeSelectors';
import BreadcrumbedComponent from '@components/BreadcrumbedComponent/BreadcrumbedComponent';
import LazyLoadingWrapper from '@components/LazyLoadingWrapper/LazyLoadingWrapper';
import { PublicTransitLayerEntityName } from '@api/entities/replancity_PublicTransitLine';
import NavigateWithSearch from '@components/NavigateWithSearch/NavigateWithSearch';
import { EntityServiceName } from '@api/enums/enums';
import RouteChecker from '@features/public-transit-editor/pt-route-check/ui/RouteChecker';
import MapTransitFilters from '@features/public-transit-editor/map-filters/MapTransitFilters';
import GroupSelection from '../../widgets/group-selection/GroupSelection';
import GroupSelectionForm from '@features/group-selection/ui/GroupSelectionForm';

const PublicTransitEditor = React.lazy(
    () => import('@pages/ProjectModes/PublicTransitEditorModePage')
);

export const useTransitRoutes = () => {
    return (
        <Route path="transit" element={<LazyLoadingWrapper element={<PublicTransitEditor />} />}>
            {useTransitStopsRoutes()}
            <Route index element={<NavigateWithSearch to="lines" replace />} />
            <Route element={<BreadcrumbedComponent />}>
                <Route
                    path="lines"
                    handle={{ crumb: (data: any) => ({ name: 'All lines', ...data }) }}
                >
                    <Route index element={<TransitLinesList />} />
                    <Route path="new" element={<TransitLineEdit />} />
                    <Route
                        path=":lineId"
                        element={<TransitLine />}
                        loader={({ params }) => params}
                        handle={{
                            crumb: ({ lineId }) => ({
                                label: 'Line',
                                id: lineId,
                                entityName: PublicTransitLayerEntityName.LINES,
                            }),
                        }}
                    >
                        <Route index element={<TransitRoutesList />} />
                        <Route path="edit" element={<TransitLineEdit />} />
                        <Route
                            path="new"
                            element={<TransitRouteEdit />}
                            handle={{ crumb: (data: any) => ({ name: 'New route', ...data }) }}
                        />
                        <Route
                            path=":routeId"
                            element={<TransitRoute />}
                            loader={({ params }) => params}
                            handle={{
                                crumb: ({ routeId }) => ({
                                    label: 'Route',
                                    id: routeId,
                                    entityName: PublicTransitLayerEntityName.ROUTES,
                                }),
                            }}
                        >
                            <Route index element={<TransitRouteStopsList />} />
                            <Route path=":editMode" element={<TransitRouteEdit />} />
                            <Route path="departures" element={<TransitRouteDeparturesList />} />
                            <Route path="departures/new" element={<DepartureEdit />} />
                            <Route
                                path="departures/:departureId/edit"
                                element={<DepartureEdit />}
                            />
                        </Route>
                    </Route>
                </Route>
                <Route
                    path="checkroute"
                    element={<RouteChecker entityServiceName={EntityServiceName.PUBLIC_TRANSIT} />}
                />
                <Route path="mapfilters" element={<MapTransitFilters />} />
            </Route>
            <Route
                path="layers"
                element={
                    <LayersListWrapper>
                        <LayersTimeSelectors />
                    </LayersListWrapper>
                }
            />
            <Route
                path="groupselection"
                element={
                    <GroupSelection
                        entityName={'replancity_TransitUpdateRoute'}
                        sourceEntityName={PublicTransitLayerEntityName.ROUTES}
                        groupUpdateTaskArgument="transitRouteUpdateTask"
                        showDeleteBtn={false}
                    />
                }
            >
                <Route path=":groupselectionId?" element={<GroupSelectionForm />} />
            </Route>
        </Route>
    );
};
